/**
 * useContractAgreements — one contract's signing model, at page level.
 *
 * EXTRACTED FROM `AgreementsBoard` WITHOUT A CHANGE OF BEHAVIOUR. Every query below kept its
 * key, its enabled condition, its stale time and its refetch predicate; the slot derivation is
 * the same `buildBoardSlots` call on the same inputs. What moved is only WHERE it lives: the
 * board is one surface among several that need these seven answers, and a second surface that
 * re-declared them would register a second query function against keys this one already owns —
 * the failure mode that made the insurance read return two different shapes.
 *
 * WHAT DELIBERATELY DID NOT MOVE: the per-row `['agreement-render', documentId]` query. That
 * cache entry IS the shown-hash record — every RPC that freezes text compares against the hash
 * the admin was displayed — so it stays where the row that displays it lives, lazily, one call
 * per rendered row.
 */

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import { driverProfileName } from '@/lib/driverProfile';
import { captureError } from '@/lib/logger';
import { parseAgreementError } from '@/lib/agreements/constants';
import {
    fetchAgreementDocumentArcs,
    fetchAgreementDocuments,
    fetchAgreementTokens,
    type AgreementDocumentRow,
} from '@/lib/agreements/api';
import { TEMPLATE_KEY_BY_INSTRUMENT } from '@/lib/agreements/instrument';
import { fetchAgreementTemplates } from '@/lib/agreements/templates';
import {
    instrumentKeyGap,
    newerPublishedVersion,
    type InstrumentKeyGap,
    type TemplateVersionGap,
} from '@/lib/agreements/templateCatalog';

import {
    contractDepositsKey,
    depositSubjectsFor,
    useContractDeposits,
} from '@/components/contracts/setup/data/deposits';

import { buildBoardSlots, documentsAreOut, type BoardSlot } from './boardSlots';
import {
    agreementBoardSubjectsKey,
    agreementBoardSubjectsKeyFor,
    fetchAgreementBoardSubjects,
    pickPartnerAgreement,
    type JunctionScope,
} from './boardSubjects';

/** How often the roster is re-read while a document is out for signature. */
const POLL_INTERVAL_MS = 10_000;

/**
 * `vehicles.partner_id`, off whichever shape the embed came back as.
 *
 * A TO-ONE EMBED IS AN OBJECT AND A TO-MANY IS AN ARRAY, and PostgREST decides which from the
 * REQUEST rather than from the schema — the same both-ways reading the witness embed above takes.
 * Undefined and null collapse to null, which `isThirdPartyVehicle` reads as "no third-party
 * vehicle here" rather than as Primelane's own fleet.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function vehiclePartnerIdOf(contract: any): number | null {
    const vehicle = Array.isArray(contract?.vehicles) ? contract.vehicles[0] : contract?.vehicles;
    return vehicle?.partner_id ?? null;
}

/**
 * The contract row this hook holds — the parties, and the witness as the templates print them.
 *
 * EXPORTED BECAUSE IT IS WHAT A WITNESS EDIT HAS TO INVALIDATE. The parties block writes
 * `contract_witnesses` directly and the only reader of that row on the page is this entry, so a
 * hand-spelled key at the write site would be a silent no-op the moment either string moved.
 */
export const agreementBoardContractKey = (contractId: number) =>
    ['agreement-board-contract', contractId] as const;

/**
 * The subjects entry and its prefix, RE-EXPORTED FROM WHERE THE READ NOW LIVES.
 *
 * The key belongs beside the query function that owns it (`boardSubjects.ts`), and the six writers
 * that invalidate it import it from here — so it is re-exported rather than moved, and a writer's
 * import stays a writer's import.
 */
export {
    agreementBoardSubjectsKey,
    AGREEMENT_BOARD_SUBJECTS_KEY,
    type JunctionScope,
} from './boardSubjects';

/**
 * How every agreement failure is reported: logged, then shown as the server's own sentence.
 *
 * The RPCs raise CODE-prefixed messages that name what is wrong (a missing merge field, a
 * hash that no longer matches), and paraphrasing them loses the only thing an admin can act
 * on — so `parseAgreementError` is what reaches the toast, verbatim.
 */
export function reportAgreementError(error: unknown) {
    captureError(error);
    toast.error(parseAgreementError(error).message);
}

interface UseContractAgreementsOptions {
    contractId: number;
    /**
     * WHICH JUNCTIONS' INSURANCE LOANS BECOME SLOTS [D-B45]. Defaults to `'setup'`.
     *
     * `'setup'` is the setup board's scope and the one every existing caller gets: the id-earliest
     * junction's loans [D-B17], under the key this hook has always used. `'all'` is the renewal
     * door on the Insurance tab of Contract Insights — every junction's loans, under a key of its
     * own, so a surface that is about year three can offer the signing ceremony for year three.
     *
     * THE DEFAULT IS THE NARROW ONE, AND THAT MATTERS MORE THAN THE ERGONOMICS. A caller that has
     * not thought about renewals gets [D-B17] settled rather than a second year's rows arriving on
     * a canvas that grades one.
     */
    junctionScope?: JunctionScope;
    /** Called after any refresh, so the page that owns completion can re-read it. */
    onSuccess?: () => void;
}

export function useContractAgreements({
    contractId,
    junctionScope = 'setup',
    onSuccess,
}: UseContractAgreementsOptions) {
    const queryClient = useQueryClient();

    // ── The contract and its parties ─────────────────────────────────────────
    /**
     * ITS LOADING AND ERROR STATES ARE READ, AND THEY HAVE TO BE. `slots` returns `[]` until BOTH
     * this row and the subjects have landed, so a surface that asked only the subjects can be told
     * the board is ready while it is still empty — and answer "there is nothing here" about a
     * contract it has not finished reading.
     */
    const {
        data: contract,
        isLoading: contractLoading,
        isError: contractFailed,
    } = useQuery({
        queryKey: agreementBoardContractKey(contractId),
        queryFn: async () => {
            const { data, error } = await supabase
                .from('contracts')
                .select(
                    // The witness's phone and address are read, not just their name: all three print
                    // on the witness agreement — the only instrument that names them since [D-B18]
                    // — and the manifest's Parties row is what tells an admin one is missing before
                    // a send is refused.
                    //
                    // `cash_price` IS READ FOR THE SAME KIND OF REASON THE PARTIES ARE: it is a
                    // precondition of the hire purchase agreement rather than a decoration on it,
                    // and a board that could not see it would offer to generate a Schedule with the
                    // charge for credit left blank.
                    // `profiles.admin_id` IS SELECTED SO THE RIGHT PROFILE CAN BE PICKED. The
                    // embed is unique only where `admin_id IS NULL`, and the name resolved from it
                    // is the name shown against every slot on the signing board.
                    // THE VEHICLE'S OWNER AND THE FEE ARE READ HERE, NOT IN `boardSubjects` [D-B48].
                    // Both are contract facts of exactly the kind `cash_price` is — preconditions
                    // of an instrument rather than decorations on it — and this read happens ONCE
                    // per contract while the subjects read happens once per `JunctionScope`. The
                    // vehicle embed is the only way to `vehicles.partner_id`, which is what decides
                    // whether a Vehicle Schedule is owed at all and what locks the hire purchase
                    // agreement until its partner has signed.
                    'id, driver_id, witness_id, cash_price, management_rate, vehicle_id, ' +
                    'vehicles(id, partner_id), ' +
                    'drivers(profiles(id, admin_id, first_name, last_name)), contract_witnesses(id, name, phone, address)',
                )
                .eq('id', contractId)
                .single();
            if (error) throw error;
            return data as any;
        },
        enabled: !!contractId,
    });

    const driverName = driverProfileName(contract?.drivers?.profiles);
    const witnessRow = Array.isArray(contract?.contract_witnesses)
        ? contract?.contract_witnesses[0]
        : contract?.contract_witnesses;
    const witnessName = witnessRow?.name ?? null;

    /**
     * The deposits, from the ONE read that owns them.
     *
     * THEY USED TO BE FETCHED HERE TOO, and that second reading is what made a deposit linked
     * from the setup page show no deposit agreement until a hard refresh: the deposit mutation
     * invalidated the deposit keys, this board's subjects key was not one of them, and the two
     * lists drifted. Now the deposit area and the board read the same rows.
     */
    const { data: contractDeposits } = useContractDeposits(contractId);

    /**
     * The subjects a document can be generated for, AT THE SCOPE THIS SURFACE ASKED FOR [D-B45].
     *
     * THE READ AND THE RULE LIVE IN `boardSubjects.ts`, where a test can reach them; what is left
     * here is which scope this caller wants and where the answer is cached. The two are one
     * decision — `agreementBoardSubjectsKeyFor` and `fetchAgreementBoardSubjects` take the same
     * scope — so a widened read cannot land on the narrow key, which is the one way this option
     * could hurt: `'setup'`'s entry is graded by the manifest, and a renewal year's loans arriving
     * in it is contract 137's "✓ and Not on file" contradiction all over again.
     */
    const {
        data: subjects,
        isLoading: subjectsLoading,
        isError: subjectsFailed,
    } = useQuery({
        queryKey: agreementBoardSubjectsKeyFor(contractId, junctionScope),
        queryFn: () => fetchAgreementBoardSubjects(contractId, junctionScope),
        enabled: !!contractId,
    });

    /**
     * THE SAME SUBJECTS AT FULL WIDTH — FOR THE CITATION CASCADE, AND FOR NOTHING ELSE [D-B44].
     *
     * WHAT A HIRE PURCHASE VOID TAKES WITH IT IS A FACT ABOUT THE CONTRACT, NOT ABOUT THE CANVAS
     * ASKING. Both insurance instruments quote `{{contract.hire_purchase_reference}}` in text that
     * is frozen at the first signature (`CITING_INSTRUMENTS`), so withdrawing the master leaves
     * EVERY live one of them naming a reference nobody can produce — the [D-B43] state the cascade
     * exists to prevent. Computed off the setup board, the cascade can only see the id-earliest
     * junction's instrument [D-B17]; the renewal door this release opens makes the others reachable,
     * so a sealed year-three Record would be left behind by a void that reported it had carried
     * everything.
     *
     * SO THE CENSUS IS WIDENED AND THE GRADING IS NOT. `slots` stays exactly the scope the caller
     * asked for — the manifest, the ledger, readiness and every area partition consume that one and
     * only that one — and `cascadeSlots` below is a SECOND board, off these rows, that nothing
     * grades. At `junctionScope: 'all'` this entry IS the subjects entry: same key, same query
     * function, one fetch, and `cascadeSlots` hands back `slots` unchanged.
     *
     * IT SHARES THE RENEWAL DOOR'S CACHE ENTRY, so a setup canvas and a Contract Insights tab open
     * on one contract read these rows once between them, and `refreshBoard`'s prefix invalidation
     * reaches both.
     */
    const { data: cascadeSubjects } = useQuery({
        queryKey: agreementBoardSubjectsKeyFor(contractId, 'all'),
        queryFn: () => fetchAgreementBoardSubjects(contractId, 'all'),
        enabled: !!contractId,
    });

    // ── The documents themselves ─────────────────────────────────────────────
    const {
        data: documents,
        isLoading: documentsLoading,
        isError: documentsFailed,
    } = useQuery({
        queryKey: ['agreement-documents', contractId],
        queryFn: () => fetchAgreementDocuments(contractId),
        enabled: !!contractId,
        // Polls only while something is actually out: a board of completed documents, or
        // one where nothing has been generated, has nothing to learn from a refetch.
        refetchInterval: (query) =>
            documentsAreOut((query.state.data ?? []) as AgreementDocumentRow[])
                ? POLL_INTERVAL_MS
                : false,
    });

    const { data: arcs } = useQuery({
        queryKey: ['agreement-document-arcs', contractId],
        queryFn: () => fetchAgreementDocumentArcs(contractId),
        enabled: !!contractId,
    });

    /**
     * The template catalogue, shared with the templates page under the same key.
     *
     * Only the version numbers are wanted here: a document pins the version that was
     * published when it was generated, so a version published afterwards changes nothing
     * about it — and the admin has no way to notice that unless the row says so.
     */
    const { data: templates } = useQuery({
        queryKey: ['agreement-templates'],
        queryFn: fetchAgreementTemplates,
        staleTime: 5 * 60 * 1000,
    });

    /**
     * IS THE INSURANCE PREMIUM PAYMENT RECORD PUBLISHED ON THIS DATABASE? [D-B45]
     *
     * THE REGISTRY, NOT THE MONEY, DECIDES WHETHER THIS BOARD MAY NAME A RECORD. `agreement_create`
     * selects the template BY KEY, so until a PUBLISHED `insurance_payment` row exists the server
     * raises an Advance on every insurance loan however fully deposited it is — and a board naming
     * the Record in that window walks an admin through a send confirmation for one document and
     * hands the driver another. The gate self-activates: the migration publishes the row, the next
     * catalogue read sees it, and the board starts naming Records with nothing deployed here.
     *
     * OFF THE CATALOGUE ALREADY IN THIS BUNDLE rather than a second read of the same table. The
     * templates query above returns every version of every key with its `status`, which answers
     * this exactly — and a dedicated `limit 1` beside it would be a second network round trip, a
     * second cache entry and a second thing to invalidate for one boolean. While it is in flight
     * the answer is `false`, which is the conservative half.
     */
    const recordPublished = useMemo(
        () =>
            (templates ?? []).some(
                (row) =>
                    row.template_key === TEMPLATE_KEY_BY_INSTRUMENT.PAYMENT_RECORD &&
                    row.status === 'published',
            ),
        [templates],
    );

    const documentIds = useMemo(() => (documents ?? []).map((doc) => doc.id), [documents]);

    const { data: tokens } = useQuery({
        queryKey: ['agreement-tokens', contractId, documentIds],
        queryFn: () => fetchAgreementTokens(documentIds),
        enabled: documentIds.length > 0,
    });

    // ── Who is countersigning ────────────────────────────────────────────────
    const { data: signingProfile } = useQuery({
        queryKey: ['my-signing-profile'],
        queryFn: async () => {
            const { data: auth } = await supabase.auth.getUser();
            if (!auth?.user) return null;
            const { data, error } = await (supabase as any)
                .from('profiles')
                .select('id, first_name, last_name, signature_path, admins(role)')
                .eq('user_id', auth.user.id)
                .maybeSingle();
            if (error) throw error;
            return data;
        },
        staleTime: 60_000,
    });

    const signerName = signingProfile
        ? `${signingProfile.first_name ?? ''} ${signingProfile.last_name ?? ''}`.trim() || null
        : null;
    const signerRole = Array.isArray(signingProfile?.admins)
        ? signingProfile?.admins[0]?.role ?? null
        : signingProfile?.admins?.role ?? null;

    // ── Slots ────────────────────────────────────────────────────────────────
    /**
     * Everything a board is built from EXCEPT the subjects — which is the only thing the two
     * boards below disagree about. Named once so they cannot drift apart in any other way.
     */
    const boardBase = useMemo(
        () =>
            contract
                ? {
                    contractId,
                    driverId: contract.driver_id ?? null,
                    witnessId: contract.witness_id ?? null,
                    cashPrice: contract.cash_price ?? null,
                    /*
                     * PostgREST hands a to-one embed back as an object and a to-many as an array,
                     * and which one arrives is decided by the REQUEST rather than by the schema —
                     * so both readings are accepted here, exactly as the witness embed above is
                     * read both ways. `vehicles.partner_id` is NOT NULL, so the only null this can
                     * produce is a contract that names no vehicle.
                     */
                    vehiclePartnerId: vehiclePartnerIdOf(contract),
                    /*
                     * READ BESIDE THE OWNER, NOT INSTEAD OF IT [D-B48]. The driver-signature gate
                     * requires the referent to be about the contract's CURRENT vehicle as well as
                     * its current owner, and a partner with two cars satisfies the owner limb on
                     * either — so the board needs both halves to answer the question the server asks.
                     */
                    vehicleId: contract.vehicle_id ?? null,
                    managementRate: contract.management_rate ?? null,
                    deposits: depositSubjectsFor(contractDeposits),
                    documents: documents ?? [],
                    arcs: arcs ?? [],
                    recordPublished,
                }
                : null,
        [contract, contractDeposits, documents, arcs, contractId, recordPublished],
    );

    const slots = useMemo<BoardSlot[]>(() => {
        if (!boardBase || !subjects) return [];
        return buildBoardSlots({
            ...boardBase,
            contractAgreements: subjects.contractAgreements,
            insuranceLoans: subjects.insuranceLoans,
            /*
             * THE STANDING ADVANCES, AS THE SCOPE ANSWERED THEM (owner's ruling, 2026-09-16). The
             * read returns `[]` at `'setup'` without going out, so this line is [D-B17] kept
             * rather than widened: the setup canvas mints no LOAN slot because the subjects it was
             * handed carry none.
             */
            loans: subjects.loans,
            /*
             * THE PICK HAPPENS HERE BECAUSE THE VEHICLE'S OWNER IS KNOWN HERE [D-B48]. The read
             * takes every referent on the contract — a reassigned vehicle leaves the previous
             * owner's sealed row behind — and `pickPartnerAgreement` chooses the current owner's,
             * else the unsealed one. Keying the read itself on the partner would put a partner id
             * in the cache key, where a reassignment strands the old entry.
             */
            partnerAgreement: pickPartnerAgreement(
                subjects.partnerAgreements,
                boardBase.vehiclePartnerId,
            ),
        });
    }, [boardBase, subjects]);

    /**
     * THE BOARD THE CITATION CASCADE READS — every junction, whatever this caller is about [D-B44].
     *
     * IT IS A CENSUS, NOT A CANVAS. Nothing renders it, nothing grades it and no area partitions it;
     * `AgreementSlotList` walks it to answer one question — what does withdrawing the hire purchase
     * agreement leave quoting a dead reference — and `citationCascade` keeps only the members whose
     * text is already frozen.
     *
     * THE FALLBACK IS THE NARROW BOARD, WHICH IS THE BEHAVIOUR THAT PRECEDED THIS. While the widened
     * read is in flight the cascade answers off `slots`, so it can be short but never wrong about
     * what it names; the dialog and the mutation read the same memo, so what an admin was shown is
     * what was withdrawn.
     */
    const cascadeSlots = useMemo<BoardSlot[]>(() => {
        if (junctionScope === 'all') return slots;
        if (!boardBase || !cascadeSubjects) return slots;
        return buildBoardSlots({
            ...boardBase,
            contractAgreements: cascadeSubjects.contractAgreements,
            insuranceLoans: cascadeSubjects.insuranceLoans,
            /*
             * AND THE STANDING ADVANCES, WHICH THE CENSUS NEEDS AS MUCH AS THE RENEWAL YEARS
             * [D-B44]. `loan v7` quotes `{{contract.hire_purchase_reference}}` three times and its
             * text freezes at the first signature, so a sealed advance left behind by a hire
             * purchase void is a live instrument naming a reference nobody can produce — exactly
             * the [D-B43] state the cascade exists to prevent. This entry reads at `'all'`, so the
             * advances are here whatever scope the caller asked for; a cascade fed only the
             * caller's board would report it had carried everything.
             */
            loans: cascadeSubjects.loans,
            partnerAgreement: pickPartnerAgreement(
                cascadeSubjects.partnerAgreements,
                boardBase.vehiclePartnerId,
            ),
        });
    }, [junctionScope, slots, boardBase, cascadeSubjects]);

    const arcById = useMemo(
        () => new Map((arcs ?? []).map((arc) => [arc.id, arc])),
        [arcs],
    );

    /**
     * PENDING_REVIEW ONLY, AND ON PURPOSE. Before the first signature nothing is frozen and
     * void-and-reissue costs nothing; from IN_SIGNING onwards a party has already attested to
     * the old wording, so pointing at a newer version would be inviting an admin to throw
     * away signatures that are already valid.
     */
    const templateGap = useCallback(
        (slot: BoardSlot): TemplateVersionGap | null => {
            const doc = slot.document;
            if (!doc || doc.status !== 'PENDING_REVIEW') return null;
            return newerPublishedVersion(arcById.get(doc.id)?.template_id, templates ?? []);
        },
        [arcById, templates],
    );

    /**
     * THE SAME QUESTION ONE AXIS ACROSS [D-B57]: not "has this family been re-cut" but "is this
     * still the right family". `agreement_create`'s insurance selector picks between three pages
     * off the loan's own rows, so a loan that gains or loses an instalment row moves the page the
     * SAME document type would now be generated on — and the document keeps the one it pinned.
     *
     * PENDING_REVIEW ONLY, FOR `templateGap`'s REASON AND ONE OF ITS OWN. Nothing is frozen before
     * the first signature, so void-and-reissue costs nothing; and from IN_SIGNING onwards the
     * SERVER is already saying this out loud — `agreement_sign` recomputes the same key on both
     * arms and refuses with `AGREEMENT_INSTRUMENT_MISMATCH`, in a sentence that names both shapes.
     * The window this hint fills is the one a `missing[]` block never reaches that refusal in.
     *
     * `slot.expectedTemplateKey` IS NULL ON EVERY NON-INSURANCE ROW, which is what scopes this to
     * the three insurance pages without a doc-type test here — see `BoardSlot.expectedTemplateKey`.
     */
    const instrumentGap = useCallback(
        (slot: BoardSlot): InstrumentKeyGap | null => {
            const doc = slot.document;
            if (!doc || doc.status !== 'PENDING_REVIEW') return null;
            return instrumentKeyGap(
                arcById.get(doc.id)?.template_id,
                templates ?? [],
                slot.expectedTemplateKey,
            );
        },
        [arcById, templates],
    );

    const refreshBoard = useCallback(() => {
        // THE CONTRACT ROW IS ONE OF THE BOARD'S READS, not the page's, and what it carries decides
        // whether a slot may be generated at all: the parties it names, and the cash price the hire
        // purchase agreement cannot be minted without. A money commit one canvas away moves that
        // answer, so a refresh that re-read only the documents would leave the row blocked over a
        // figure the admin had just filled in.
        queryClient.invalidateQueries({ queryKey: agreementBoardContractKey(contractId) });
        queryClient.invalidateQueries({ queryKey: ['agreement-documents', contractId] });
        queryClient.invalidateQueries({ queryKey: ['agreement-document-arcs', contractId] });
        // THE CONTRACT'S SUBJECTS AT EVERY SCOPE, FROM ONE LINE [D-B45]. React Query matches by
        // prefix, and the renewal door's entry is the setup key plus a trailing segment — so this
        // reaches both, and a document generated from the Insurance tab refreshes a setup board
        // mounted in another tab rather than leaving it holding a slot that is no longer empty.
        queryClient.invalidateQueries({ queryKey: agreementBoardSubjectsKey(contractId) });
        // The deposit rows carry the legacy `contract_deposit_agreements` arc, so a document
        // created or voided here changes them as surely as it changes the board's own reads.
        queryClient.invalidateQueries({ queryKey: contractDepositsKey(contractId) });
        queryClient.invalidateQueries({ queryKey: ['agreement-tokens', contractId] });
        // THE SEAL STATE IS A READINESS INPUT, and it is the one the server and the screen used to
        // disagree about: the three agreement arms of `get_contract_setup_status` read the legacy
        // signed_at + agreement_url pair, which finalize writes. Creating, voiding, signing or
        // sealing a document moves that answer, and the witness this board sets moves
        // WITNESS_NOT_ASSIGNED with it.
        queryClient.invalidateQueries({ queryKey: ['contract-setup-status', contractId] });
        onSuccess?.();
    }, [queryClient, contractId, onSuccess]);

    // ── The witness, which both contract documents freeze ────────────────────
    const setWitness = useMutation({
        mutationFn: async (witnessId: number | null) => {
            const { error } = await supabase
                .from('contracts')
                .update({ witness_id: witnessId })
                .eq('id', contractId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Witness saved');
            queryClient.invalidateQueries({ queryKey: agreementBoardContractKey(contractId) });
            refreshBoard();
        },
        onError: reportAgreementError,
    });

    return {
        contract,
        driverName,
        witnessName,
        /** The witness as the witness agreement prints them — name, phone and address [D-B18]. */
        witness: witnessRow
            ? {
                id: (witnessRow.id ?? null) as number | null,
                name: (witnessRow.name ?? null) as string | null,
                phone: (witnessRow.phone ?? null) as string | null,
                address: (witnessRow.address ?? null) as string | null,
            }
            : null,
        witnessId: (contract?.witness_id ?? null) as number | null,
        subjects,
        documents,
        arcs,
        templates,
        /** Whether this database can raise an Insurance Premium Payment Record at all [D-B45]. */
        recordPublished,
        tokens,
        signingProfile,
        signerName,
        signerRole,
        slots,
        /**
         * THE CENSUS THE CITATION CASCADE READS — every junction, whatever `junctionScope` is
         * [D-B44]. Never rendered, never graded, never partitioned; see its derivation above.
         */
        cascadeSlots,
        templateGap,
        /** Whether the document on a slot was generated on a page its subject no longer calls for. */
        instrumentGap,
        refreshBoard,
        setWitness,
        /**
         * THE THREE READS `slots` IS BUILT FROM, AND ALL THREE OF THEM. The contract row was
         * missing from this: `slots` needs it as much as it needs the subjects, so a caller could
         * be told the board had finished loading while it was still `[]`.
         */
        loading: contractLoading || subjectsLoading || documentsLoading,
        /**
         * ONE OF THOSE THREE CAME BACK REFUSED, so `slots` is empty for a reason that is not "this
         * contract has nothing". A surface that turns an empty board into a SENTENCE has to be able
         * to tell the two apart — an unread board must not be reported as an absent instrument.
         */
        failed: contractFailed || subjectsFailed || documentsFailed,
    };
}
